import { DistributionSlice, MetricCardModel, MetricSeverity } from '../models/dashboard.model';

const DIA_MS = 24 * 60 * 60 * 1000;

const fmtNumero = new Intl.NumberFormat('pt-BR');
const fmtMoeda = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * Aceita datas **inequívocas** (ano primeiro): `yyyy-mm-dd`, opcionalmente
 * com hora e timezone, ex.: `2026-08-11T00:00:00Z`, `2026-08-11`. Aceita
 * também `yyyy-mm-dd hh:mm[:ss[.frac]]` — com **espaço** no lugar do `T` —
 * porque é o formato padrão de datetime do Laravel/PHP (`created_at`/
 * `updated_at` chegam assim da API de produção; só `expected_date`, que é
 * data pura, chegava no formato que a regex antiga aceitava). Continua sendo
 * ano-mês-dia primeiro, então não introduz a ambiguidade que motivou rejeitar
 * `/` (ver abaixo) — é só um separador diferente para o mesmo formato.
 *
 * Qualquer string com `/` continua sendo rejeitada de propósito: `new
 * Date('11/08/2026')` é lido pelo `Date` nativo como `MM/DD/YYYY` (8 de
 * novembro), não `DD/MM/YYYY` (11 de agosto) — e não lança, não avisa, só
 * devolve uma data errada em silêncio. Uma data ausente (null) é um buraco
 * visível que quem consome pode tratar; uma data ambígua resolvida errado é
 * um bug que só aparece meses depois, no relatório errado. Por isso a função
 * rejeita o que não sabe ler com certeza em vez de adivinhar.
 *
 * Vazio/null/undefined → null. String inválida → null. Nunca lança — linha
 * com data suja não pode derrubar a tela.
 */
const DATE_TIME_RE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

export function parseDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  if (value.includes('/')) return null;
  if (!DATE_TIME_RE.test(value)) return null;
  // O construtor `Date` só garante (pelo ECMA-262) interpretar como hora
  // LOCAL o formato `yyyy-mm-ddThh:mm:ss` sem timezone — o separador espaço
  // (`yyyy-mm-dd hh:mm:ss`) está fora da gramática ISO 8601 e o comportamento
  // é implementation-defined: no V8 (Node/Chrome) hoje já dá o mesmo
  // resultado do `T`, mas nada garante isso em outro motor (ex.: Safari é
  // conhecido por recusar formatos de data não padronizados). Por isso
  // normaliza o espaço para `T` antes de construir a `Date`, em vez de
  // confiar no comportamento não especificado.
  //
  // ⚠️ E a data PURA (`yyyy-mm-dd`, sem hora) recebe `T00:00:00` pelo mesmo
  // motivo, invertido: o ECMA-262 manda interpretar data-só como **UTC**,
  // enquanto data-com-hora-sem-fuso é **local**. Sem esta normalização, a
  // mesma função devolveria instantes em fusos diferentes conforme a string
  // tivesse hora ou não — e em UTC-3 `new Date('2025-08-01')` é 31/07 às
  // 21:00 local (medido). Consequências reais no que esta base já faz com o
  // resultado: `expected_date` é data pura, então `estaAtrasado`/
  // `venceEmAte7Dias` erravam por até três horas em torno da virada do dia; e
  // um `due` sem hora cairia no BALDE DO MÊS ANTERIOR no fluxo de caixa,
  // colocando dinheiro no mês errado sem nenhum sinal.
  //
  // Todas as datas deste domínio são datas comerciais brasileiras, sem fuso
  // embutido: meia-noite local é a leitura certa para todas elas.
  const comHora = value.includes(' ') ? value.replace(' ', 'T') : value;
  const normalizado = comHora.includes('T') ? comHora : `${comHora}T00:00:00`;
  const d = new Date(normalizado);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Dias inteiros entre duas datas.
 *
 * Assume dias de 24h; não normaliza fuso nem horário de verão — é uma divisão
 * simples por `DIA_MS`. O Brasil não tem DST desde 2019, mas este utilitário
 * é genérico e não faz essa suposição por conta própria.
 *
 * Devolve negativo quando `fim < inicio`. Quem usa isso para "vencido há N
 * dias" precisa filtrar o resultado por `> 0` — esta função não decide o que
 * é "vencido", só faz a subtração.
 */
export function diasEntre(inicio: Date, fim: Date): number {
  return Math.floor((fim.getTime() - inicio.getTime()) / DIA_MS);
}

export function contarPercentual(parte: number, total: number): number {
  return total > 0 ? Math.round((parte / total) * 100) : 0;
}

export function formatarNumero(n: number): string {
  return fmtNumero.format(n);
}

/** Dinheiro. O NBSP que o Intl insere é normalizado para espaço comum. */
export function formatarMoeda(n: number): string {
  return fmtMoeda.format(n).replace(/\u00a0/g, ' ');
}

/**
 * Converte o `value` monetário de um relatório de agregação (formato
 * brasileiro: separador de milhar `.`, decimal `,`) para `number`.
 *
 * ⚠️ Existe porque `parseFloat` direto numa string BR mente sem avisar.
 * `parseFloat` para no primeiro caractere que não reconhece como parte de
 * um número e trata `.` como ponto decimal — medido em runtime, 12/08/2026,
 * contra o payload real de `installments-report`:
 *
 *   parseFloat("15.708.862,11")  →  15.708   (deveria ser 15708862.11)
 *   parseFloat("1.819.436,58")   →  1        (deveria ser 1819436.58)
 *   parseFloat("1.000,00")       →  1        (deveria ser 1000)
 *
 * Sem erro de sintaxe, sem exceção, sem NaN — um card de "R$ 15,7 milhões em
 * atrasados" mostraria "R$ 15,71", fator de um milhão errado, e o teste
 * escrito com a mesma crença ("parseFloat resolve") passaria do mesmo jeito.
 * Por isso esta função remove o separador de milhar e troca a vírgula
 * decimal por ponto ANTES de converter — nunca entrega a string BR direto
 * para `parseFloat`/`Number`.
 *
 * Ausência (`""`, `null`, `undefined`) devolve `null`, nunca `0`: um card de
 * atrasados mostrando "R$ 0,00" afirma que está tudo em dia; `null` diz que
 * a API não respondeu ou não trouxe o campo — fatos diferentes, e só quem
 * monta o card (não esta função) pode decidir o que fazer com a ausência.
 *
 * Texto que sobra inválido após a normalização também devolve `null` — nunca
 * lança, nunca deixa `NaN` escapar para `formatarMoeda` (que produziria
 * "R$ NaN").
 *
 * ⚠️ Decisão sobre formato SEM separador de milhar (ex.: "96" ou "96.5"):
 * não foi observado em nenhuma medição real — Task 1 do plano mostra que a
 * API sempre manda duas casas decimais com vírgula, mesmo para valores
 * exatos ("0,00", "96,00"). Ainda assim, a regra desta função é "vírgula é
 * decimal, ponto é milhar" (formato brasileiro), não "todo ponto é milhar" —
 * então, na AUSÊNCIA de vírgula, um único ponto é tratado como decimal
 * (convenção numérica comum, igual à do `Number` nativo): "96.5" → 96.5,
 * "96" → 96. Isso evita a leitura ambígua de "1.234" (poderia ser 1234 ou
 * 1,234) resolvendo para decimal em vez de adivinhar milhar — e string com
 * MAIS de um ponto e nenhuma vírgula (ex.: "1.234.567", formato que também
 * nunca foi medido) vira `NaN` → `null`, porque `Number` rejeita múltiplos
 * pontos: prefere admitir que não sabe ler a adivinhar errado.
 */
export function parseValorBR(value: string | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const texto = value.trim();
  if (texto === '') return null;

  // Com vírgula: formato brasileiro completo. '.' é sempre separador de
  // milhar aqui — remover todos antes de trocar a vírgula decimal por ponto.
  // Sem vírgula: não observado na API; ver decisão no docstring acima.
  const normalizado = texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto;

  const numero = Number(normalizado);
  return Number.isFinite(numero) ? numero : null;
}

export function formatarDias(n: number): string {
  return `${fmtNumero.format(n)}d`;
}

export function metricCard(input: {
  key: string;
  label: string;
  value: string;
  rawValue: number | null;
  severity: MetricSeverity;
  icon: string;
  route?: string;
  filters?: Record<string, unknown>;
}): MetricCardModel {
  const { route, filters, ...resto } = input;
  // `label` do alvo é o mesmo rótulo do card (ex.: "Meus atrasados") — é o
  // que uma faixa "mostrando X" usa para descrever o recorte sem precisar
  // reinterpretar `filters`.
  return { ...resto, target: route && filters ? { route, filters, label: input.label } : null };
}

/**
 * Ordena por contagem, mantém os `limite` maiores e soma o resto em "Outros".
 * O total sempre fecha — é o que evita gráfico cuja soma não bate com o card.
 *
 * `contagem` agrupa pela chave que o chamador escolher — tipicamente um
 * **id** (ex.: `developer_id`), mas quem chama pode degradar para uma chave
 * de descrição/nome quando o id não vier no payload (ver `byStatus`/
 * `byDeveloper` em `ticket-dashboard.builder.ts`). `rotuloDe`, quando
 * fornecido, traduz essa chave para exibição; sem ele a própria chave é
 * usada como rótulo (comportamento anterior preservado).
 *
 * `filtroDe` pode devolver `undefined` para uma chave específica — vira
 * `target: null` só naquela fatia (ex.: grupo sem id cujo `ticketIds`
 * excedeu o limite de reprodução exata). As demais fatias não são afetadas.
 *
 * `formatarValor`, quando fornecido, preenche `formattedValue` de cada fatia
 * (inclusive "Outros") — usado quando `count` carrega uma soma em dinheiro em
 * vez de uma contagem, para que cada dashboard não precise formatar por conta
 * própria.
 *
 * Empate de contagem entre duas chaves é desfeito pela ordem de inserção do
 * Map (`sort` é estável e a chave de comparação é só a contagem).
 */
export function topNComOutros(
  contagem: Map<string, number>,
  total: number,
  limite: number,
  filtroDe: (chave: string) => Record<string, unknown> | undefined,
  route: string,
  opcoes?: {
    rotuloDe?: (chave: string) => string;
    formatarValor?: (n: number) => string;
  }
): DistributionSlice[] {
  const rotuloDe = opcoes?.rotuloDe;
  const formatarValor = opcoes?.formatarValor;

  const ordenado = [...contagem.entries()].sort((a, b) => b[1] - a[1]);
  const principais = ordenado.slice(0, limite);
  const resto = ordenado.slice(limite).reduce((acc, [, n]) => acc + n, 0);

  const saida: DistributionSlice[] = principais.map(([chave, n]) => {
    const filtros = filtroDe(chave);
    const rotulo = rotuloDe ? rotuloDe(chave) : chave;
    return {
      key: chave,
      label: rotulo,
      count: n,
      percent: contarPercentual(n, total),
      // `label` do alvo é o rótulo da própria fatia — mesmo raciocínio de
      // `metricCard`, para uma faixa "mostrando X" poder descrever o recorte.
      target: filtros ? { route, filters: filtros, label: rotulo } : null,
      ...(formatarValor ? { formattedValue: formatarValor(n) } : {}),
    };
  });

  if (resto > 0) {
    saida.push({
      key: '__outros',
      label: 'Outros',
      count: resto,
      percent: contarPercentual(resto, total),
      target: null,
      ...(formatarValor ? { formattedValue: formatarValor(resto) } : {}),
    });
  }
  return saida;
}
